/**
 * artworkPlacement.js — where the ARTWORK goes, as opposed to where the die went.
 *
 * Pure affine algebra + the prepress transform chain. No React, no DOM, no
 * transport (mirrored to artworkPlacement.cjs by tools/gen-cjs-mirrors.cjs).
 *
 * ═══ THE DEFECT THIS EXISTS TO FIX ═════════════════════════════════════════
 *
 * `/export/vector/pdf` used to take a corner and an angle per placement, which
 * quietly assumes the nested piece IS the artwork's own die. When prepress runs
 * it is not. Prepress wraps the carton in flaps, and the piece MNE nested is the
 * BLANK — the artwork sits inside it at the flap offset.
 *
 * Measured on a real Bottom-Lock carton (Hepaguard through prepress_v1):
 *
 *     operator artwork   228.00 x 214.00 mm
 *     emitted blank      240.00 x 264.50 mm
 *     artwork sits at    (12.00, 38.50)      = (glue flap, bottom flap)
 *
 * So every stamp on every prepress plate landed 12 mm left and 38.5 mm low. Not
 * a tolerance problem — a wrong plate.
 *
 * And it is worse than an offset. A mirrored input pose is a REFLECTION:
 * prepress reports `pose.matrix` linear part (-1, 0, 0, 1), det -1. No
 * (x, y, rotation) triple can express a reflection, so for those cartons the
 * old placement model was not imprecise, it was incapable.
 *
 * ═══ THE CHAIN ═════════════════════════════════════════════════════════════
 *
 * Prepress publishes the whole thing, exactly, in `report.cartons[].transforms`
 * (prepress_v1/transforms.py, Phase 8 — written for precisely this reason:
 * "the frontend could not place artwork or export a PDF without guessing"):
 *
 *     artwork --pose--> base L --blank_origin--> dieline --member--> tile
 *
 * and then the layout puts the piece on the sheet. Composing those gives ONE
 * affine from the artwork's own frame to sheet millimetres, which is what the
 * PDF endpoint now accepts.
 *
 * ═══ FRAMES (the part that must not be got wrong) ══════════════════════════
 *
 * The artwork's frame is the DIE BBOX LOWER-LEFT AT THE ORIGIN, in mm, Y-up.
 * Three separate things agree on it, which is what makes this composable:
 *
 *   · `sources[].vertices` — the detected die, "local origin", as handed to
 *     prepress. Built BEFORE prepress runs (pipeline.py), so it is the artwork,
 *     not the blank.
 *   · prepress's `pose` input — the outline as supplied.
 *   · the PDF Form XObject — `_page_to_form_xobject` sets
 *     `/Matrix [1 0 0 1 -x0 -y0]`, moving the die region's lower-left to (0,0).
 *
 * ═══ CONVENTION ════════════════════════════════════════════════════════════
 *
 * Matrices are `[a, b, c, d, tx, ty]` ROW-VECTOR, matching prepress exactly:
 *
 *     x' = a*x + b*y + tx ;  y' = c*x + d*y + ty
 *
 * This is NOT PDF's `cm` order. The wire carries the row-vector form so it
 * agrees with the chain it came from, and the backend transposes once, in
 * `Placement.cm()`, where the change is visible.
 */

/** Row-vector affine. Identity. */
export const AFFINE_IDENTITY = [1, 0, 0, 1, 0, 0];

/** Geometric tolerance, mm — far below press tolerance, far above fp noise. */
export const GEOM_EPS = 1e-9;

/** Is this a usable 6-number affine? */
export function isAffine(m) {
  return Array.isArray(m) && m.length === 6 && m.every((n) => Number.isFinite(n));
}

/** det of the linear part. Negative means the map includes a reflection. */
export function affineDet(m) {
  return m[0] * m[3] - m[1] * m[2];
}

/** Apply to a point. */
export function affineApply(m, x, y) {
  return [m[0] * x + m[1] * y + m[4], m[2] * x + m[3] * y + m[5]];
}

/** Apply to a vertex list. */
export function affineApplyAll(m, pts) {
  return pts.map(([x, y]) => affineApply(m, x, y));
}

/**
 * `first` THEN `second` — pipeline order, reading left to right.
 *
 * The classic transform bug is getting this backwards, and it is silent: the
 * result is still a valid affine and still plates something. prepress_v1's own
 * `Affine.then` is defined the same way for the same reason; this mirrors it.
 */
export function affineCompose(first, second) {
  const [a1, b1, c1, d1, tx1, ty1] = first;
  const [a2, b2, c2, d2, tx2, ty2] = second;
  return [
    a2 * a1 + b2 * c1,
    a2 * b1 + b2 * d1,
    c2 * a1 + d2 * c1,
    c2 * b1 + d2 * d1,
    a2 * tx1 + b2 * ty1 + tx2,
    c2 * tx1 + d2 * ty1 + ty2,
  ];
}

/** Compose a list, left to right. */
export function affineChain(...ms) {
  return ms.filter(isAffine).reduce((acc, m) => affineCompose(acc, m), AFFINE_IDENTITY);
}

/** CCW rotation about the origin. Quarter turns are exact (see editorDoc). */
export function affineRotation(deg) {
  const a = ((Number(deg) || 0) % 360 + 360) % 360;
  if (a === 0) return [1, 0, 0, 1, 0, 0];
  if (a === 90) return [0, -1, 1, 0, 0, 0];
  if (a === 180) return [-1, 0, 0, -1, 0, 0];
  if (a === 270) return [0, 1, -1, 0, 0, 0];
  const r = (a * Math.PI) / 180;
  const c = Math.cos(r), s = Math.sin(r);
  return [c, -s, s, c, 0, 0];
}

export function affineTranslation(tx, ty) {
  return [1, 0, 0, 1, tx, ty];
}

/**
 * Inverse. Used to carry a clip rectangle back from the base-L frame (where
 * prepress states panel extents) into the die-local frame (where the PDF
 * content stream needs it). Every transform in this chain is a right-angle
 * rotation, an optional mirror and a translation, so det is +/-1 and this is
 * exact — but the general form is written out because a silent wrong inverse
 * would misplace a clip rather than fail.
 */
export function affineInverse(m) {
  const det = affineDet(m);
  if (!Number.isFinite(det) || Math.abs(det) < 1e-12) return null;
  const [a, b, c, d, tx, ty] = m;
  const ia = d / det, ib = -b / det, ic = -c / det, id = a / det;
  return [ia, ib, ic, id, -(ia * tx + ib * ty), -(ic * tx + id * ty)];
}

/** Two affines equal to within `tol`? Used to detect a resequenced panel order. */
export function affineEquals(m, n, tol = 1e-6) {
  return isAffine(m) && isAffine(n) && m.every((v, i) => Math.abs(v - n[i]) <= tol);
}

/* ══════════════════════════════════════════════════════════════════════════
 * Reading the chain out of a result
 * ════════════════════════════════════════════════════════════════════════ */

/**
 * The band's lower-left corner in a tuck carton's own base frame.
 *
 * WHY THIS IS NEEDED AT ALL
 * -------------------------
 * BL publishes `blank_origin`, measured from the base L's own origin, so its
 * chain is just pose then that. RTE and STE publish `flap_frame` instead, and
 * it is measured from the BAND — the row of panels — not from the base outline.
 * The two differ by the depth of the customer's own lower tab, which hangs below
 * the band. Composing pose with `flap_frame` directly puts a measured RTE
 * carton's artwork 23 mm above its blank, sticking out of the die.
 *
 * The band is the maximal full-width horizontal strip: at any height inside it
 * the outline spans the whole carton, and above or below it only the tab column
 * does. That is found by scanline here rather than read off a field, because no
 * field carries it — and it is checked against the published wall height, so a
 * shape this does not understand reports nothing instead of a wrong answer.
 *
 * The first vertex is NOT a substitute. `canonical_tuck_vertices` documents RTE
 * as listed from the band's bottom-left corner, and STE's left-aligned variant
 * starts at its tab instead — that shortcut works on one family and silently
 * misplaces the other.
 *
 * Verified against the full RTE + STE corpus (11 cartons): in every one the
 * band lands exactly on the blank's published glue rect.
 */
export function bandOrigin(baseOutline, wallHeightMm) {
  if (!Array.isArray(baseOutline) || baseOutline.length < 3) return null;
  if (!(wallHeightMm > 0)) return null;
  const xs = baseOutline.map((p) => p[0]);
  const x0 = Math.min(...xs);
  const width = Math.max(...xs) - x0;
  const ys = Array.from(new Set(baseOutline.map((p) => p[1]))).sort((a, b) => a - b);

  let lo = Infinity, hi = -Infinity;
  for (let i = 0; i < ys.length - 1; i++) {
    const y0 = ys[i], y1 = ys[i + 1];
    if (y1 - y0 < GEOM_EPS) continue;
    const ym = (y0 + y1) / 2;
    const cuts = [];
    for (let k = 0; k < baseOutline.length; k++) {
      const [ax, ay] = baseOutline[k];
      const [bx, by] = baseOutline[(k + 1) % baseOutline.length];
      if ((ay > ym) !== (by > ym)) cuts.push(ax + ((bx - ax) * (ym - ay)) / (by - ay));
    }
    if (!cuts.length) continue;
    if (Math.abs((Math.max(...cuts) - Math.min(...cuts)) - width) < 1e-6) {
      lo = Math.min(lo, y0);
      hi = Math.max(hi, y1);
    }
  }
  if (!Number.isFinite(lo)) return null;
  // The strip we found must BE the band. If it is not, say nothing.
  if (Math.abs((hi - lo) - wallHeightMm) > 1e-6) return null;
  return [x0, lo];
}

/** A carton whose artwork placement cannot be expressed, and why. */
function unusable(name, mode, reason) {
  return { name, mode, artworkToBlank: null, members: null, reason };
}

/**
 * The artwork→blank affine for every carton prepress touched, keyed by name.
 *
 * `prepress_report.cartons[].name` IS the `source_id` (contract §3.4), so the
 * map is keyed by the same string placements carry. A carton absent from the
 * map had no prepress: its nested piece IS its die, and the corner form is
 * exactly right for it.
 *
 * Two shapes are refused rather than approximated:
 *
 *   RESEQUENCED PANELS  C37 in prepress_v1/transforms.py: "Artwork maps PER
 *     PANEL, not per dieline. A resequenced variant permutes the panels, so one
 *     translation is correct only for the standard order." When the panels do
 *     not all share `blank_origin`, no single affine can place this artwork —
 *     it would need one clipped stamp per panel. Refusing is the only honest
 *     answer; a matrix here would put whole panels in the wrong place.
 *
 *   PREPRESS TILES  a fused pair's members are resequenced blanks carrying
 *     their own panel maps, and the member chain is not verifiable from what
 *     the response publishes today. (This is separate from a pair the operator
 *     builds in the editor, which is two stamps of the SAME standard blank and
 *     composes fine.)
 */
export function prepressChains(result) {
  const out = new Map();
  const cartons = result?.prepress_report?.cartons;
  if (!Array.isArray(cartons)) return out;

  for (const entry of cartons) {
    if (!entry || typeof entry !== 'object') continue;
    const name = String(entry.name);
    const mode = entry.mode || null;
    const tr = entry.transforms;
    if (!tr || typeof tr !== 'object') {
      // prepress ran but published no chain — an older package. We know the die
      // moved and not by how much, which is the one case we must not guess at.
      out.set(name, unusable(name, mode,
        'prepress ran but published no transform chain (package too old)'));
      continue;
    }

    const pose = tr.pose?.matrix;
    if (!isAffine(pose)) {
      out.set(name, unusable(name, mode, 'transform chain is missing pose'));
      continue;
    }

    // BASE FRAME -> BLANK FRAME. Two families, two names, and they are NOT
    // interchangeable:
    //
    //   BL   `blank_origin` — measured from the base L's own origin.
    //   RTE/STE  `flap_frame` — measured from the BAND, which sits above the
    //        customer's lower tab. Using it raw puts the artwork out of the die
    //        by that tab's depth (23 mm on a measured RTE carton).
    //
    // So for a tuck carton the band's own offset is removed first. Both
    // families end up with one affine meaning the same thing.
    let origin = tr.blank_origin?.matrix;
    if (!isAffine(origin)) {
      const flap = tr.flap_frame?.matrix;
      const baseOut = entry.assets?.base_outline?.vertices_mm
        || entry.assets?.base_l?.vertices_mm;
      const band = isAffine(flap) ? bandOrigin(baseOut, entry.wall_height_mm) : null;
      if (!band) {
        out.set(name, unusable(name, mode, isAffine(flap)
          ? 'its band could not be located in the base outline, so the flap '
            + 'frame cannot be anchored'
          : 'transform chain is missing blank_origin and flap_frame'));
        continue;
      }
      origin = affineCompose(affineTranslation(-band[0], -band[1]), flap);
    }

    // The artwork outline in the BASE-L frame — pose applied to the die the
    // operator supplied. Panel extents are stated in that frame, so this is what
    // a clip is measured against. Taken from `sources[]` rather than the
    // `base_l` asset because a SourceResult always has vertices, and one
    // derivation cannot disagree with itself.
    const die = dieOutline(result, name);
    const baseL = die ? affineApplyAll(pose, die) : null;

    const groups = panelGroups(tr.panels, origin, pose, baseL);
    if (!groups) {
      out.set(name, unusable(name, mode,
        'its panel map could not be reduced to stamps (missing panel rects '
        + 'or die outline)'));
      continue;
    }

    const members = readTileMembers(tr.tile_members, pose, baseL, origin);

    out.set(name, {
      name,
      mode,
      // The standard blank: pose then the shared blank origin. Kept as the
      // fast path and as what a NON-tiled placement of this carton uses.
      artworkToBlank: affineCompose(pose, origin),
      // One entry per group of panels sharing a transform; `clip` is null when
      // the group covers the whole die and nothing needs masking.
      groups,
      // Present only when prepress fused this carton into a tile.
      members,
      pose,
      baseL,
      reason: null,
    });
  }
  return out;
}

/** The die-local outline the artwork Form XObject is normalised to. */
function dieOutline(result, sourceId) {
  const src = (result?.sources || []).find((s) => s?.source_id === sourceId);
  const v = src?.vertices;
  return (Array.isArray(v) && v.length >= 3) ? v : null;
}

function bboxOf(pts) {
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
}

/**
 * Reduce a panel map to the fewest stamps that place it correctly.
 *
 * A panel's `transform` carries everything attached to it — its flap tower, its
 * dust flap — so panels that share a transform can be painted together. Sorting
 * by source x and merging equal-transform RUNS is what turns member B's four
 * panels into two stamps instead of four, and member A's four into one stamp
 * with no clip at all.
 *
 * The clip is the panel run's full-height COLUMN of the base L, not the panel
 * face rect. `source_rect` is 157 mm tall on a carton whose artwork is 214 mm
 * tall: a Bottom-Lock's top tab sits above the Back panel and belongs to it.
 * Clipping to the face would have thrown that tab away on every resequenced
 * stamp — silently, because the rest of the panel would still look right.
 *
 * Returns null when the map cannot be read; [] never happens for a valid map.
 */
export function panelGroups(panels, blankOrigin, pose, baseL) {
  if (!Array.isArray(panels) || panels.length === 0) {
    // No panel map published. The blank origin is the whole answer only when
    // nothing was permuted, which is exactly what an absent map means.
    return isAffine(blankOrigin) ? [{ clip: null, transform: blankOrigin }] : null;
  }
  if (panels.some((p) => !isAffine(p?.transform))) return null;

  // The ordinary carton: every panel moves together, so there is nothing to
  // split and no clip to measure. Answered before source rects are even looked
  // at, so a package that publishes transforms without rects still plates.
  const first = panels[0].transform;
  if (panels.every((p) => affineEquals(p.transform, first))) {
    return [{ clip: null, transform: first }];
  }

  const read = [];
  for (const p of panels) {
    const r = p?.source_rect_mm;
    if (!Array.isArray(r) || r.length !== 4) return null;
    read.push({ x0: r[0], x1: r[0] + r[2], transform: p.transform });
  }
  read.sort((a, b) => a.x0 - b.x0);

  const runs = [];
  for (const p of read) {
    const last = runs[runs.length - 1];
    if (last && affineEquals(last.transform, p.transform)
        && Math.abs(last.x1 - p.x0) < 1e-6) {
      last.x1 = Math.max(last.x1, p.x1);
    } else {
      runs.push({ x0: p.x0, x1: p.x1, transform: p.transform });
    }
  }

  // One run covering the whole die needs no clip — and that is the ordinary
  // carton, whose output is then byte-identical to the unclipped path.
  if (runs.length === 1) return [{ clip: null, transform: runs[0].transform }];

  if (!baseL) return null;
  const [, by0, , by1] = bboxOf(baseL);
  const inv = affineInverse(pose);
  if (!inv) return null;
  // A generous vertical margin: the column must catch anything attached above
  // or below the panel face, and the die's own outline bounds what can be
  // painted anyway.
  const pad = Math.max(1, (by1 - by0)) * 2;
  return runs.map((r) => ({
    transform: r.transform,
    // Carried back into die-local space, where the content stream needs it.
    // pose is dihedral, so this rectangle stays a rectangle.
    clip: affineApplyAll(inv, [
      [r.x0, by0 - pad], [r.x1, by0 - pad], [r.x1, by1 + pad], [r.x0, by1 + pad],
    ]),
  }));
}

/** Per-member panel maps for a prepress-fused tile, or null when not tiled. */
export function readTileMembers(tileMembers, pose, baseL, blankOrigin) {
  if (!Array.isArray(tileMembers) || !tileMembers.length) return null;
  const out = [];
  for (const m of tileMembers) {
    if (!isAffine(m?.transform)) return null;
    // `panels` here is the MEMBER's own map (C37: a BL member is a resequenced
    // blank and carries its own), published by transforms.tile_blocks.
    //
    // RTE and STE tiles are SELF-mated — both halves are the same profile, only
    // rotated and offset — so they publish no per-member map and the carton's
    // own base->blank affine serves both. Passing it here is what makes a tuck
    // tile placeable at all.
    const groups = panelGroups(m.panels, blankOrigin, pose, baseL);
    if (!groups) return null;
    out.push({
      role: m.role || null,
      member: Number.isInteger(m.member) ? m.member : out.length,
      variant: m.variant || null,
      // member dieline frame -> tile frame
      transform: m.transform,
      groups,
    });
  }
  return out.sort((a, b) => a.member - b.member);
}

/**
 * The affine that put a placed piece on the sheet, recovered from the piece
 * itself: rotate about the origin by `rotationDeg`, then translate.
 *
 * Derived from the geometry rather than read from `translation`, because the
 * same function then serves the editor — where the operator has moved the piece
 * and the engine's translation is long stale — and because one derivation that
 * both paths share cannot drift from itself.
 *
 * It relies on the emitted blank being anchored at its own bbox minimum, which
 * is what prepress emits (measured: blank bbox [0, 0, 240, 264.5]) and what the
 * editor's prototypes preserve.
 */
export function blankToSheet(placedVertices, rotationDeg) {
  if (!Array.isArray(placedVertices) || placedVertices.length < 3) return null;
  const rot = affineRotation(rotationDeg);
  // Un-rotate to recover the piece's own frame, then measure where that frame's
  // origin lands once rotated back — that offset is the translation.
  const inv = affineRotation(-(Number(rotationDeg) || 0));
  const local = affineApplyAll(inv, placedVertices);
  let mx = Infinity, my = Infinity;
  for (const [x, y] of local) { if (x < mx) mx = x; if (y < my) my = y; }
  const [ox, oy] = affineApply(rot, mx, my);
  return affineCompose(rot, affineTranslation(ox, oy));
}

/**
 * Every stamp needed to paint one placed piece's artwork.
 *
 * Returns `[{ matrix, clip }]` — usually one entry, more when the piece is a
 * prepress tile or carries a resequenced panel order. Null when the carton has
 * no chain (no prepress), meaning the corner form is exactly right for it.
 *
 * The composition, outermost last:
 *
 *     plain blank   pose · panelGroup · placement
 *     tile member   pose · panelGroup · member · placement
 *
 * `cartons` is what says which: a piece worth two cartons is the fused tile and
 * must be painted through its members, and a piece worth one is the ordinary
 * blank — the SAME source emits both (`H__pair` and `H`), so the count is the
 * only thing that can tell them apart.
 *
 * Still throws when a chain exists and cannot be expressed. Silently plating
 * the wrong geometry is the failure this module exists to prevent.
 */
export function artworkStamps(chain, placeAffine, cartons = 1) {
  if (!chain) return null;
  const refuse = (why) => {
    const err = new Error(`Cannot place artwork for "${chain.name}": ${why}.`);
    err.code = 'artwork_placement_unavailable';
    err.carton = chain.name;
    throw err;
  };
  if (chain.reason) refuse(chain.reason);
  if (!isAffine(placeAffine)) return null;

  if (cartons > 1) {
    if (!chain.members || !chain.members.length) {
      refuse('it is worth more than one carton but publishes no tile members, '
        + 'so its halves cannot be told apart');
    }
    const out = [];
    for (const m of chain.members) {
      const toSheet = affineCompose(m.transform, placeAffine);
      for (const g of m.groups) {
        out.push({
          matrix: affineCompose(chain.pose, affineCompose(g.transform, toSheet)),
          clip: g.clip,
          member: m.member,
          role: m.role,
        });
      }
    }
    return out;
  }

  const groups = chain.groups || [{ clip: null, transform: null }];
  return groups.map((g) => ({
    matrix: g.transform
      ? affineCompose(chain.pose, affineCompose(g.transform, placeAffine))
      : affineCompose(chain.artworkToBlank, placeAffine),
    clip: g.clip,
    member: 0,
    role: null,
  }));
}

/* ══════════════════════════════════════════════════════════════════════════
 * Where a prepress carton is CUT and FOLDED
 *
 * The press sheet knocks out every carton's CUT outline before its second
 * pass, and its marks sit in line with every FOLD of a carton (the printer's
 * rule, backend vendor/prepress/marks.py). For a carton prepress built,
 * neither is in the uploaded artwork: it is cut as the flapped BLANK and
 * folded where prepress laid the panels. The report publishes both — this
 * reads them. (The marks go round the PRINTED carton, not the blank; the
 * backend builds that outline from the stamps themselves.)
 * ════════════════════════════════════════════════════════════════════════ */

const FOLD_EPS = 1e-6;

/**
 * The panel row of a blank as [x, y, w, h] rects, in that blank's own frame.
 *
 * BL publishes the rects (`transforms.panels[].dest_rect_mm`; a resequenced
 * tile member carries its own). RTE and STE publish none: their row starts at
 * the flap frame's offset (the glue flap's width, the lower tab's depth), runs
 * `panel_order_mm` wide and `wall_height_mm` tall — the same numbers the
 * report's flap regions are laid out from.
 */
function panelRects(entry, member) {
  const own = member && Array.isArray(member.panels) && member.panels.length ? member.panels : null;
  const panels = own || entry?.transforms?.panels;
  if (Array.isArray(panels) && panels.length) {
    const out = [];
    for (const pnl of panels) {
      const r = pnl?.dest_rect_mm;
      if (!Array.isArray(r) || r.length !== 4 || !r.every((v) => Number.isFinite(v))) return null;
      out.push(r.map(Number));
    }
    return out;
  }
  const widths = entry?.panel_order_mm;
  const off = entry?.transforms?.flap_frame?.offset_mm;
  const h = Number(entry?.wall_height_mm);
  if (!Array.isArray(widths) || !widths.length || !Array.isArray(off) || off.length < 2
      || !Number.isFinite(h)) return null;
  let x = Number(off[0]);
  const y = Number(off[1]);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  const out = [];
  for (const w of widths) {
    const wn = Number(w);
    if (!Number.isFinite(wn)) return null;
    out.push([x, y, wn, h]);
    x += wn;
  }
  return out;
}

/**
 * A prepress blank's fold lines, [[x0, y0], [x1, y1]] each, in the blank's own
 * frame (or a tile member's, when `member` is its `tile_members[]` entry).
 *
 *   · every panel boundary, the height of the panel row — the creases between
 *     panels and the glue flap's crease at the row's end;
 *   · the row's top and bottom, where the flaps are hinged;
 *   · each flap region's hinge: the edge of its rect that faces the row (a
 *     BL tuck is hinged on the lid above the Back panel, not on the row).
 *
 * Null when the report does not say enough to know.
 */
export function blankFolds(entry, member = null) {
  const rects = panelRects(entry, member);
  if (!rects || !rects.length) return null;
  const x0 = Math.min(...rects.map((r) => r[0]));
  const x1 = Math.max(...rects.map((r) => r[0] + r[2]));
  const y0 = Math.min(...rects.map((r) => r[1]));
  const y1 = Math.max(...rects.map((r) => r[1] + r[3]));
  const xs = [];
  for (const r of rects) {
    for (const x of [r[0], r[0] + r[2]]) {
      if (!xs.some((v) => Math.abs(v - x) < FOLD_EPS)) xs.push(x);
    }
  }
  xs.sort((a, b) => a - b);
  const folds = xs.map((x) => [[x, y0], [x, y1]]);
  folds.push([[x0, y0], [x1, y0]], [[x0, y1], [x1, y1]]);
  const regions = (member && member.flap_regions ? member.flap_regions : entry?.flap_regions)?.regions;
  for (const reg of (Array.isArray(regions) ? regions : [])) {
    const r = reg?.rect_mm;
    if (!Array.isArray(r) || r.length !== 4 || !r.every((v) => Number.isFinite(v))) continue;
    const [rx, ry, rw, rh] = r.map(Number);
    if (ry >= y1 - FOLD_EPS) folds.push([[rx, ry], [rx + rw, ry]]);
    else if (ry + rh <= y0 + FOLD_EPS) folds.push([[rx, ry + rh], [rx + rw, ry + rh]]);
    else if (rx + rw <= x0 + FOLD_EPS) folds.push([[rx + rw, ry], [rx + rw, ry + rh]]);
    else if (rx >= x1 - FOLD_EPS) folds.push([[rx, ry], [rx, ry + rh]]);
  }
  return folds;
}

/**
 * Every carton in one placed prepress piece: its cut outline and its fold
 * lines, in SHEET mm — `{ member, outline, folds }` per carton.
 *
 * `placeAffine` is the one that put the piece on the sheet (blankToSheet, or
 * the editor's); a tile's members go through their own `transform` first,
 * exactly as their artwork does in `artworkStamps`. The outline is the one
 * the backend already carried into sheet space (`placed_vertices`, or a
 * tile's `components[]`): the die the press CUTS, which the knockout needs.
 *
 * Null when the carton had no prepress or the piece cannot be placed.
 */
export function placedCuts(result, sourceId, placeAffine, cartons, placement) {
  const entry = reportEntryFor(result, sourceId);
  if (!entry || !isAffine(placeAffine)) return null;
  const mapSeg = (m) => (seg) => affineApplyAll(m, seg);
  if (cartons > 1) {
    const members = [...(Array.isArray(entry.transforms?.tile_members)
      ? entry.transforms.tile_members : [])]
      .sort((a, b) => (Number(a?.member) || 0) - (Number(b?.member) || 0));
    if (!members.length) return null;
    const comps = Array.isArray(placement?.components) ? placement.components : [];
    const out = [];
    members.forEach((m, k) => {
      if (!isAffine(m?.transform)) return;
      const member = Number.isInteger(m.member) ? m.member : k;
      const comp = comps.find((c) => c && c.member === member) || (comps.length === members.length ? comps[k] : null);
      const inTile = m.outline_in_tile?.vertices_mm;
      const outline = Array.isArray(comp?.placed_vertices) && comp.placed_vertices.length >= 3
        ? comp.placed_vertices
        : (Array.isArray(inTile) && inTile.length >= 3 ? affineApplyAll(placeAffine, inTile) : null);
      if (!outline) return;
      const folds = blankFolds(entry, m);
      const toSheet = affineCompose(m.transform, placeAffine);
      out.push({ member, outline, folds: folds ? folds.map(mapSeg(toSheet)) : [] });
    });
    return out;
  }
  const outline = placement?.placed_vertices;
  if (!Array.isArray(outline) || outline.length < 3) return null;
  const folds = blankFolds(entry);
  return [{ member: 0, outline, folds: folds ? folds.map(mapSeg(placeAffine)) : [] }];
}

/** Back-compat single-matrix form, for callers that know a piece is simple. */
export function artworkMatrix(chain, placeAffine) {
  const stamps = artworkStamps(chain, placeAffine, 1);
  return stamps && stamps.length === 1 && !stamps[0].clip ? stamps[0].matrix : null;
}

/** Round a matrix for the wire — 9 dp is far below any press tolerance. */
export function roundMatrix(m) {
  return m.map((v) => Math.round(v * 1e9) / 1e9);
}

/**
 * Cartons on this sheet whose artwork cannot be placed, with the reason.
 *
 * Lets a caller say so BEFORE the operator clicks export and waits, instead of
 * throwing at the end of the flow. Only cartons that actually appear in
 * `placements` are reported — a chain that cannot be expressed for a carton
 * nobody nested is not this sheet's problem.
 */
export function unplaceableCartons(result) {
  const chains = prepressChains(result);
  if (!chains.size) return [];
  const used = new Set(
    (result?.placements || [])
      .map((p) => p.source_id)
      .filter(Boolean),
  );
  const out = [];
  for (const [name, chain] of chains) {
    if (!used.has(name) || !chain.reason) continue;
    out.push({ name, reason: chain.reason });
  }
  return out;
}


/* ══════════════════════════════════════════════════════════════════════════
 * Asking prepress for a tile
 * ════════════════════════════════════════════════════════════════════════ */

/**
 * Keys on a report's `config` that belong to the DEPLOYMENT, not the request.
 *
 * `required_passthrough` decides which component owns firm order lines, and
 * `allow_tiling*` is clamped by the server. Both are refused by name if a client
 * sends them — deliberately, because a silently ignored setting is
 * indistinguishable from one that was never sent. So they are dropped here,
 * where the reason can be written down, rather than provoking a 400.
 */
const DEPLOYMENT_CONFIG_KEYS = (k) => k === 'required_passthrough' || k.startsWith('allow_tiling');

/**
 * The prepress config to build a tile with, taken from what the job already ran.
 *
 * Not an optimisation — a correctness requirement. Build the tile with default
 * config while the sheet's single blanks were built with the job's, and the two
 * disagree about flap dimensions: the tile's halves would be a different die
 * from the cartons beside them, on the same plate, looking perfectly normal.
 */
export function tileConfigFrom(entry) {
  const cfg = entry?.config;
  if (!cfg || typeof cfg !== 'object') return null;
  const out = {};
  for (const [k, v] of Object.entries(cfg)) {
    if (DEPLOYMENT_CONFIG_KEYS(k)) continue;
    out[k] = v;
  }
  return Object.keys(out).length ? out : null;
}

/** The carton type prepress recorded for a source, or null if it never ran. */
export function cartonTypeFor(result, sourceId) {
  const cartons = result?.prepress_report?.cartons;
  if (!Array.isArray(cartons)) return null;
  const e = cartons.find((c) => c && String(c.name) === sourceId);
  return e?.carton_type || null;
}

/** The report entry prepress recorded for a source, or null. */
export function reportEntryFor(result, sourceId) {
  const cartons = result?.prepress_report?.cartons;
  if (!Array.isArray(cartons)) return null;
  return cartons.find((c) => c && String(c.name) === sourceId) || null;
}
